/**
 * schema.org, built from the graph.
 *
 * The point of structured data here is not decoration. This platform's whole
 * claim is that mathematics is a graph of concepts joined by prerequisites, and
 * schema.org has words for exactly that — `LearningResource.teaches`,
 * `competencyRequired`, `educationalLevel`. So the markup says the same thing
 * the product says, derived from the same edges, and a search engine can read
 * "this lesson teaches adding fractions and needs common denominators first"
 * without anyone writing that sentence twice.
 *
 * Two rules for anything added here:
 *
 * 1. **Only claim what the page shows.** Markup describing content a visitor
 *    cannot see is a manual action waiting to happen, and it is dishonest
 *    besides.
 * 2. **No ratings, no fake counts.** There are no reviews on this site, so
 *    there is no `aggregateRating`, however much it would widen a result.
 */

import type { Concept, Course, CurriculumRegistry, Lesson, LevelId } from '@yuan-math/curriculum';
import type { JsonLd, MetaLink } from './types.ts';
import { SITE, absoluteUrl } from './site.ts';
import { flatten, isoDuration, truncate } from './text.ts';

/** Stable `@id`s, so nodes on different pages refer to the same organisation. */
const ORG_ID = '#organization';
const SITE_ID = '#website';

/**
 * The publisher. `EducationalOrganization` rather than `Organization` because
 * that is what this is, and the narrower type is the more useful claim.
 */
export function organizationNode(origin: string = SITE.origin): JsonLd {
  return {
    '@type': 'EducationalOrganization',
    '@id': `${absoluteUrl('/', origin)}${ORG_ID}`,
    name: SITE.name,
    url: absoluteUrl('/', origin),
    slogan: SITE.tagline,
    description:
      'A mathematics learning platform built on a connected map of concepts, teaching from kindergarten counting to university analysis.',
    logo: absoluteUrl(SITE.image.path, origin),
    /*
     * The founder, which `/about` also states in prose. Structured data may
     * only claim what the page shows, and this is a fact about the
     * organisation rather than a rating or a review — the kind of claim this
     * file is allowed to make.
     */
    founder: { '@type': 'Person', name: 'Ethan Yuan' },
  };
}

export function webSiteNode(origin: string = SITE.origin): JsonLd {
  return {
    '@type': 'WebSite',
    '@id': `${absoluteUrl('/', origin)}${SITE_ID}`,
    name: SITE.name,
    url: absoluteUrl('/', origin),
    inLanguage: SITE.lang,
    publisher: { '@id': `${absoluteUrl('/', origin)}${ORG_ID}` },
  };
}

/**
 * A breadcrumb trail.
 *
 * The trail passed in is the one drawn on the page — the `<nav class="breadcrumb">`
 * every content page already renders — because markup that describes a
 * different path than the one shown is the first rule broken.
 */
export function breadcrumbNode(trail: MetaLink[], origin: string = SITE.origin): JsonLd {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: trail.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.label,
      item: absoluteUrl(crumb.path, origin),
    })),
  };
}

/** Human-readable level names for a list of level ids. */
function levelNames(registry: CurriculumRegistry, levels: readonly LevelId[]): string[] {
  return levels
    .map((id) => registry.level(id)?.title)
    .filter((title): title is string => typeof title === 'string');
}

/**
 * A course.
 *
 * `hasCourseInstance` is what makes this eligible for a course result rather
 * than a plain link, and the workload is real: it is the sum of the estimated
 * minutes of every lesson in the course, not a round number chosen to look
 * substantial.
 */
export function courseNode(
  registry: CurriculumRegistry,
  course: Course,
  origin: string = SITE.origin,
): JsonLd {
  const lessons = registry.lessonsOfCourse(course.id);
  const minutes = lessons.reduce((total, lesson) => total + lesson.estimatedMinutes, 0);
  const concepts = registry.conceptsOfCourse(course.id);

  return {
    '@type': 'Course',
    name: course.title,
    description: truncate(course.description, 300),
    url: absoluteUrl(`/courses/${course.id}`, origin),
    inLanguage: SITE.lang,
    provider: { '@id': `${absoluteUrl('/', origin)}${ORG_ID}` },
    educationalLevel: registry.level(course.level)?.title ?? course.level,
    teaches: concepts.slice(0, 25).map((concept) => concept.title),
    // Every lesson is free at every tier; only the volume of generated practice
    // is capped. Saying so here is both accurate and the point.
    isAccessibleForFree: true,
    offers: [{ '@type': 'Offer', category: 'Free', price: '0', priceCurrency: 'USD' }],
    hasCourseInstance: [
      {
        '@type': 'CourseInstance',
        courseMode: 'online',
        courseWorkload: isoDuration(Math.max(minutes, 60)),
        inLanguage: SITE.lang,
      },
    ],
  };
}

/**
 * A lesson.
 *
 * `teaches` names the concept, not the lesson, because the concept is what the
 * student ends up knowing and what mastery is tracked against.
 */
export function lessonNode(
  registry: CurriculumRegistry,
  lesson: Lesson,
  origin: string = SITE.origin,
): JsonLd {
  const concept = registry.concept(lesson.conceptId);
  const course = registry.courseOfLesson(lesson.id);
  const prerequisites = (concept?.prerequisites ?? [])
    .map((id) => registry.concept(id)?.title)
    .filter((title): title is string => typeof title === 'string');

  const node: JsonLd = {
    '@type': 'LearningResource',
    name: lesson.title,
    description: truncate(lesson.summary, 300),
    url: absoluteUrl(`/lessons/${lesson.id}`, origin),
    inLanguage: SITE.lang,
    learningResourceType: 'Lesson',
    educationalUse: 'instruction',
    timeRequired: isoDuration(lesson.estimatedMinutes),
    isAccessibleForFree: true,
    provider: { '@id': `${absoluteUrl('/', origin)}${ORG_ID}` },
    teaches: concept ? concept.title : lesson.title,
    about: lesson.objectives.map((objective) => flatten(objective)),
  };

  if (concept) node.educationalLevel = levelNames(registry, concept.levels);
  if (prerequisites.length > 0) node.competencyRequired = prerequisites;
  if (course) {
    node.isPartOf = {
      '@type': 'Course',
      name: course.title,
      url: absoluteUrl(`/courses/${course.id}`, origin),
    };
  }

  return node;
}

/**
 * A concept.
 *
 * Modelled as a `LearningResource` even for a `planned` concept with no lesson
 * behind it yet: the page is real, it explains where the mathematics leads, and
 * it is how somebody searching "eigenvalues" arrives. What it must not do is
 * claim to teach something it cannot — so a planned concept carries no lessons
 * and no time estimate.
 */
export function conceptNode(
  registry: CurriculumRegistry,
  concept: Concept,
  origin: string = SITE.origin,
): JsonLd {
  const prerequisites = concept.prerequisites
    .map((id) => registry.concept(id)?.title)
    .filter((title): title is string => typeof title === 'string');
  const lessons = concept.status === 'available' ? registry.lessonsForConcept(concept.id) : [];

  const node: JsonLd = {
    '@type': 'LearningResource',
    name: concept.title,
    description: truncate(concept.summary, 300),
    url: absoluteUrl(`/concepts/${concept.id}`, origin),
    inLanguage: SITE.lang,
    learningResourceType: 'Concept',
    isAccessibleForFree: true,
    provider: { '@id': `${absoluteUrl('/', origin)}${ORG_ID}` },
    teaches: concept.title,
    educationalLevel: levelNames(registry, concept.levels),
    about: { '@type': 'Thing', name: registry.strand(concept.strand)?.title ?? concept.strand },
  };

  if (prerequisites.length > 0) node.competencyRequired = prerequisites;
  if (concept.estimatedMinutes) node.timeRequired = isoDuration(concept.estimatedMinutes);
  if (lessons.length > 0) {
    node.hasPart = lessons.map((lesson) => ({
      '@type': 'LearningResource',
      name: lesson.title,
      url: absoluteUrl(`/lessons/${lesson.id}`, origin),
    }));
  }

  return node;
}

/** An ordered list of internal pages — the courses index, a strand of the map. */
export function itemListNode(
  name: string,
  items: MetaLink[],
  origin: string = SITE.origin,
): JsonLd {
  return {
    '@type': 'ItemList',
    name,
    numberOfItems: items.length,
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      url: absoluteUrl(item.path, origin),
    })),
  };
}

/**
 * The plans.
 *
 * An `AggregateOffer` rather than one `Product` per tier: there is a single
 * product here sold at several prices, and four `Product` nodes on one page is
 * how a pricing table starts claiming to be a shop.
 */
export function offerNode(
  plans: { name: string; price: number }[],
  origin: string = SITE.origin,
): JsonLd {
  const prices = plans.map((plan) => plan.price);
  return {
    '@type': 'Product',
    name: `${SITE.name} subscription`,
    description:
      'Every lesson, the full Math Map, mastery tracking and spaced review are free. A subscription lifts the daily cap on generated practice.',
    brand: { '@id': `${absoluteUrl('/', origin)}${ORG_ID}` },
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: 'USD',
      lowPrice: Math.min(...prices, 0).toFixed(2),
      highPrice: Math.max(...prices, 0).toFixed(2),
      offerCount: plans.length,
      availability: 'https://schema.org/InStock',
    },
  };
}

/**
 * Wraps nodes for embedding. Every emitted node is given `@context` here rather
 * than by its builder, so builders stay composable inside `hasPart` and friends.
 */
export function asDocument(node: JsonLd): JsonLd {
  return { '@context': 'https://schema.org', ...node };
}
